import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Step,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  Stepper,
  Text,
  useSteps,
  useToast,
} from "@chakra-ui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ResponseOf } from "../../../core/api";
import useApi from "../../../shared/hooks/useApi";
import useSocketEvent from "../../../shared/hooks/useSocketEvent";
import { PatientId, PatientServicePlanId } from "../../../shared/schema/schema";
import ServicePlanDetailsStep from "./ServicePlanDetailsStep";
import ServicePlanDiagnosisCodesStep from "./ServicePlanDiagnosisCodesStep";
import ServicePlanFinalizeAction from "./ServicePlanFinalizeAction";
import ServicePlanMedicationsStep from "./ServicePlanMedicationsStep";
import ServicePlanSignaturesStep from "./ServicePlanSignaturesStep";
import ServicePlanStatusTag from "./ServicePlanStatusTag";
import ServicePlanTasksStep from "./ServicePlanTasksStep";

// The endpoint inlines its response object, so the plan type comes from the
// endpoint rather than components["schemas"].
type ServicePlan = ResponseOf<"get", "./service_plans/:patientServicePlanId">;

const STEPS = [
  { title: "Details" },
  { title: "Tasks" },
  { title: "Diagnosis codes" },
  { title: "Medications" },
  { title: "Signatures" },
];

interface Props {
  isOpen: boolean;
  patientId: PatientId;
  servicePlanId: PatientServicePlanId;
  onClose: () => void;
}

export default function ServicePlanEditorModal({
  isOpen,
  patientId,
  servicePlanId,
  onClose,
}: Props) {
  const { queries } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { activeStep, setActiveStep } = useSteps({ index: 0, count: STEPS.length });

  const servicePlan = useQuery({
    ...queries.servicePlan.byId(servicePlanId),
    enabled: isOpen,
  });

  // A signee submitted through an emailed claim link. Refresh so the signature
  // appears without the agency member reopening the plan.
  useSocketEvent({
    key: "PatientServicePlanSignatureClaimed",
    onEvent: (event) => {
      if (event.patientServicePlanId !== servicePlanId) {
        return;
      }

      queryClient.invalidateQueries({
        queryKey: queries.servicePlan.byId(servicePlanId).queryKey,
      });

      toast({
        title: `Signature received from ${event.signedBy}`,
        status: "success",
        position: "top-right",
        isClosable: true,
      });
    },
  });

  return (
    <Modal isOpen={isOpen} scrollBehavior="inside" size="6xl" onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={3}>
            <Text>CDSC Service Plan</Text>
            {servicePlan.data !== undefined && (
              <ServicePlanStatusTag status={servicePlan.data.status} />
            )}
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stepper index={activeStep} marginBottom={6} size="sm">
            {STEPS.map((step, index) => (
              <Step key={step.title} onClick={() => setActiveStep(index)}>
                <StepIndicator>
                  <StepStatus
                    active={<StepNumber />}
                    complete={<StepIcon />}
                    incomplete={<StepNumber />}
                  />
                </StepIndicator>
                <Box>
                  <StepTitle>{step.title}</StepTitle>
                </Box>
                <StepSeparator />
              </Step>
            ))}
          </Stepper>

          {(() => {
            switch (servicePlan.status) {
              case "error":
                return (
                  <Alert status="error">
                    <AlertIcon />
                    <AlertTitle>Error loading service plan</AlertTitle>
                    <AlertDescription>Please try again later.</AlertDescription>
                  </Alert>
                );
              case "pending":
                return <Text>Loading service plan...</Text>;
              case "success":
                return (
                  <StepContent
                    activeStep={activeStep}
                    isReadOnly={servicePlan.data.status === "FINALIZED"}
                    patientId={patientId}
                    plan={servicePlan.data}
                    servicePlanId={servicePlanId}
                  />
                );
            }
          })()}
        </ModalBody>
        <ModalFooter>
          <Button
            isDisabled={activeStep === 0}
            variant="ghost"
            onClick={() => setActiveStep(activeStep - 1)}
          >
            Back
          </Button>
          <Spacer />
          {servicePlan.data !== undefined && (
            <ServicePlanFinalizeAction
              isLastStep={activeStep === STEPS.length - 1}
              patientId={patientId}
              plan={servicePlan.data}
              servicePlanId={servicePlanId}
            />
          )}
          <Button
            isDisabled={activeStep >= STEPS.length - 1}
            variant="ghost"
            onClick={() => setActiveStep(activeStep + 1)}
          >
            Next
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function StepContent(props: {
  activeStep: number;
  isReadOnly: boolean;
  patientId: PatientId;
  plan: ServicePlan;
  servicePlanId: PatientServicePlanId;
}) {
  const { activeStep, isReadOnly, patientId, plan, servicePlanId } = props;

  switch (activeStep) {
    case 0:
      return (
        <ServicePlanDetailsStep
          isReadOnly={isReadOnly}
          patientId={patientId}
          plan={plan}
          servicePlanId={servicePlanId}
        />
      );
    case 1:
      return (
        <ServicePlanTasksStep
          isReadOnly={isReadOnly}
          patientId={patientId}
          plan={plan}
          servicePlanId={servicePlanId}
        />
      );
    case 2:
      return <ServicePlanDiagnosisCodesStep patientId={patientId} />;
    case 3:
      return <ServicePlanMedicationsStep patientId={patientId} />;
    case 4:
      return (
        <ServicePlanSignaturesStep
          isReadOnly={isReadOnly}
          plan={plan}
          servicePlanId={servicePlanId}
        />
      );
    default:
      return null;
  }
}
